import { cn } from '../lib/cn';
import type { Split } from '../types/api';

/**
 * G.6 — small uneditable train/val/test pill shown beside an image
 * thumbnail when the parent project is locked. Hidden when split is
 * null (unlocked project). Three distinct colors so the customer can
 * scan the image list at a glance: train = neutral gray, val = amber,
 * test = purple. Matches the same coloring used in the Lock modal's
 * coverage table for consistency across surfaces.
 *
 * Stateless / read-only — no click handlers in v1. Future v1.1 could
 * make it editable for power users.
 */
interface SplitChipProps {
    split: Split | null | undefined;
    isDarkMode?: boolean;
    size?: 'xs' | 'sm';
}

const labelFor: Record<Split, string> = {
    train: 'train',
    val: 'val',
    test: 'test',
};

export default function SplitChip({
    split,
    isDarkMode = false,
    size = 'xs',
}: SplitChipProps) {
    if (!split) return null;

    const colorClasses = (() => {
        switch (split) {
            case 'train':
                return isDarkMode
                    ? 'bg-gray-700/70 text-gray-200 border border-gray-600'
                    : 'bg-gray-100 text-gray-700 border border-gray-300';
            case 'val':
                return isDarkMode
                    ? 'bg-amber-900/40 text-amber-200 border border-amber-700/70'
                    : 'bg-amber-50 text-amber-800 border border-amber-300';
            case 'test':
                return isDarkMode
                    ? 'bg-purple-900/40 text-purple-200 border border-purple-700/70'
                    : 'bg-purple-50 text-purple-800 border border-purple-300';
        }
    })();

    const sizeClasses = size === 'xs'
        ? 'text-[10px] px-1.5 py-0 leading-4'
        : 'text-xs px-2 py-0.5';

    return (
        <span
            className={cn(
                'inline-flex items-center rounded font-medium uppercase tracking-wide shrink-0',
                sizeClasses,
                colorClasses,
            )}
            title={`Dataset split: ${labelFor[split]}`}
        >
            {labelFor[split]}
        </span>
    );
}
